"use client";

import { NotebookPen } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trackCitationNoteSaved } from "@/lib/analytics";
import {
  MAX_NOTE_LENGTH,
  type SavedCitation,
  setCitationNote,
} from "@/lib/bibliography";
import { t } from "@/lib/i18n";

const WHITESPACE_RUN = /\s+/;

function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(WHITESPACE_RUN).length : 0;
}

/**
 * Per-source annotation, the thing that turns a bibliography into an *annotated*
 * bibliography.
 *
 * The site already had an annotated-bibliography tool, but it was a standalone page
 * you pasted a citation into by hand — it could not see your saved sources, and
 * nothing you wrote there was kept. Notes now live on the saved citation, so they
 * survive, travel with the entry when it is restyled, and feed the annotated export.
 *
 * The word count is shown because assignments specify one; 100–200 words is the usual
 * ask and it is genuinely hard to eyeball.
 */
export function CitationNoteEditor({
  citation,
  onSaved,
}: {
  citation: SavedCitation;
  onSaved: () => void;
}) {
  const bib = useBibStrings();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(citation.note ?? "");

  function open() {
    setDraft(citation.note ?? "");
    setEditing(true);
  }

  function save() {
    setCitationNote(citation.id, draft);
    setEditing(false);
    onSaved();
    const words = countWords(draft);
    trackCitationNoteSaved(words);
    toast.success(
      draft.trim() ? t(bib.noteSaved, { count: words }) : bib.noteRemoved
    );
  }

  function cancel() {
    setDraft(citation.note ?? "");
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          aria-label={bib.noteLabel}
          autoFocus
          className="min-h-24 text-sm"
          maxLength={MAX_NOTE_LENGTH}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              cancel();
            }
            // Ctrl/Cmd+Enter saves, matching the rest of the app's dialogs.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              save();
            }
          }}
          placeholder={bib.notePlaceholder}
          value={draft}
        />
        <div className="flex items-center gap-2">
          <Button onClick={save} size="sm">
            {bib.noteSave}
          </Button>
          <Button onClick={cancel} size="sm" variant="ghost">
            {bib.noteCancel}
          </Button>
          <span className="ml-auto text-muted-foreground text-xs">
            {t(bib.noteWords, { count: countWords(draft) })}
          </span>
        </div>
      </div>
    );
  }

  if (citation.note) {
    return (
      <button
        className="block w-full rounded-md border border-border/60 border-l-2 border-l-primary/50 bg-background/60 px-3 py-2 text-left transition-colors hover:bg-accent/50"
        onClick={open}
        title={bib.noteEdit}
        type="button"
      >
        <span className="block whitespace-pre-wrap text-muted-foreground text-sm leading-relaxed">
          {citation.note}
        </span>
        <span className="mt-1 block text-muted-foreground/70 text-xs">
          {t(bib.noteWords, { count: countWords(citation.note) })} ·{" "}
          {bib.noteClickToEdit}
        </span>
      </button>
    );
  }

  return (
    <Button
      className="text-muted-foreground text-xs"
      onClick={open}
      size="sm"
      variant="ghost"
    >
      <NotebookPen className="mr-1.5 size-3.5" />
      {bib.noteAdd}
    </Button>
  );
}
